import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { RotateCcw, TriangleAlert } from 'lucide-react';

/**
 * A deploy replaces every hashed chunk, so a tab opened before it points at
 * filenames that no longer exist. Retrying the import cannot help; only
 * re-fetching the HTML can.
 */
const STALE_CHUNK =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

const RELOAD_STAMP = 'route-chunk-reload';
const RELOAD_COOLDOWN_MS = 60_000;

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Optional reset key, e.g. the route pathname, to clear errors on navigation. */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/** Returns true when it reloads, so the caller stops rendering the error UI. */
function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP) ?? 0);
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    // Storage blocked (private mode, embedded frame): fall back to the retry UI.
    return false;
  }
}

/**
 * Class-based error boundary around the route tree: keeps a broken page from
 * taking down the whole shell, announces the failure politely and offers a
 * retry action.
 */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  override state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Route error:', error, info.componentStack);
    if (STALE_CHUNK.test(error.message ?? '')) reloadForStaleChunk();
  }

  override componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly handleRetry = (): void => {
    if (STALE_CHUNK.test(this.state.error?.message ?? '') && reloadForStaleChunk()) return;
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children } = this.props;

    if (error) {
      return (
        <div
          role="alert"
          className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center"
        >
          <TriangleAlert aria-hidden="true" className="mb-4 h-10 w-10 text-state-danger" />
          <h1 className="text-xl font-bold text-ink">This screen could not be displayed</h1>
          <p className="mt-2 max-w-prose text-sm text-ink-muted">
            {error.message || 'An unexpected problem occurred while rendering this page.'}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white shadow-card hover:bg-navy-mid focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-steel"
          >
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            Try again
          </button>
        </div>
      );
    }

    return children;
  }
}
